import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Grid, Typography, CircularProgress, Breadcrumbs, Divider, Card } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams, Link } from 'react-router-dom';
 
import InputField from 'components/formFields/InputField';
import AutocompleteField from 'components/formFields/autocompleteField';
import { useResponseHandler } from 'hooks/useResponseHandler';
import { getApplicationDataDropDownStore, getAppReleaseByIdStore, updateAppReleaseStore, setAppRelease } from 'store/app-releases';

import { appReleaseFormModel } from './formik/edit/formModels/appReleaseFormModel';
import formInitialValues from './formik/edit/formModels/formInitialValues';
import { validationSchema } from './formik/edit/formModels/validationSchema';
import { FeaturesFieldArray } from './components/FeaturesFieldArray';
import { Edit, Save } from '@mui/icons-material';
import { novedadesReleaseMapper, novedadesReleaseToFormMapper } from './mapper/novedades.maper';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: 50,
  padding: 24,
  borderRadius: 16,
  boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
  border: '1px solid rgba(145, 158, 171, 0.16)'
}));

export const EditNovedadesRelease = () => {
  const { id } = useParams();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const { applications_for_select, app_release } = useSelector((state: any) => state.appReleases);
  const { handleResponse, ResponseComponents } = useResponseHandler() as any;
  const [loadingApps, setLoadingApps] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<any>(null);

  useEffect(() => {
    fetchData();

    return () => {
      dispatch(setAppRelease(null));
    };
  }, [id, dispatch]);

  const fetchData = async () => {
    if (id) {
      const response = await dispatch(getAppReleaseByIdStore(id));
      if (!response?.ok) {
          navigate('/manager/novedades');
      }
    }
  };

  useEffect(() => {
    if (app_release) {
      setInitialFormValues(novedadesReleaseToFormMapper(app_release, formInitialValues));
    }
  }, [app_release]);

 

  const handleSubmit = async (values: typeof formInitialValues, actions: any) => {
    const mappedValues = novedadesReleaseMapper(values);
    if (!id) return;
    
    const response = await dispatch(updateAppReleaseStore(id, mappedValues));
    await handleResponse(response, 'actualizar novedad', true);
   
  };

  return (
    <Box sx={{ width: '100%' }}>
      {ResponseComponents}

      <Typography fontSize={25} color="textPrimary" sx={{ fontWeight: 700 }}>Editar Novedad (Release)</Typography>
      <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
          <Typography color="textPrimary" sx={{ fontWeight: 700 }}>Manager</Typography>
          <Link  to="/manager/novedades">
            Listar Novedades
          </Link>
          <Typography color="textPrimary">Editar Novedad</Typography>
      </Breadcrumbs>
      <Divider sx={{ marginBottom: 3 }}/>

      {!initialFormValues ? (
        <></>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialFormValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <StyledCard>
              <Form id={appReleaseFormModel.formId} autoComplete="nope">
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
                    Información General
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InputField
                        name={appReleaseFormModel.formField.version.name}
                        label={appReleaseFormModel.formField.version.label}
                        fullWidth
                        variant="outlined"
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <AutocompleteField
                        name={appReleaseFormModel.formField.application.name}
                        label={appReleaseFormModel.formField.application.label}
                        options={applications_for_select}
                        fullWidth
                      />
                      {loadingApps && <CircularProgress size={20} sx={{ mt: 1 }} />}
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 4 }} />

                <Box sx={{ mb: 2 }}>
                  <FeaturesFieldArray />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/manager/novedades')}
                    disabled={isSubmitting}
                  >
                    Atras
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    startIcon={<Edit />}
                    disabled={isSubmitting}
                  > 
                    {isSubmitting ? 'Guardando...' : 'Actualizar Novedad'}
                  </Button>
                </Box>
              </Form>
            </StyledCard>
          )}
        </Formik>
      )}
    </Box>
  );
};